import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Lock, AlertCircle, Loader2, Check, Eye, EyeOff, ArrowLeft } from "lucide-react";
import PasswordChecklist from "../../../../components/PasswordChecklist";
import { EMERALD_DARK } from "../../../../constants/colors";
import "../../LoginScreen.css";
import { forgotNewPasswordSchema, type ForgotNewPasswordInput } from "../../../../lib/validation/schemas/auth.schema";

interface ForgotNewPasswordStepProps {
  loading: boolean;
  serverError: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
}

export default function ForgotNewPasswordStep({
  loading,
  serverError,
  onSubmit,
  onBack,
}: ForgotNewPasswordStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotNewPasswordInput>({
    resolver: zodResolver(forgotNewPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onSubmit",
  });

  const watchedPassword = watch("password", "");

  return (
    <div className="ls-step-container">
      <div className="ls-step-back-wrapper">
        <button
          type="button"
          onClick={onBack}
          className="ls-back-button"
        >
          <ArrowLeft size={16} /> {t("common:actions.back")}
        </button>
      </div>

      <div className="ls-step-icon-circle">
        <Lock size={28} color={EMERALD_DARK} />
      </div>

      <h2 className="ls-step-title">
        {t("forgot.newPasswordTitle")}
      </h2>
      <p className="ls-step-text ls-step-text--last">
        {t("forgot.newPasswordSubtitle")}
      </p>

      {(errors.password?.message || errors.confirmPassword?.message || serverError) && (
        <div className="ls-error-banner ls-error-banner--step">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>
            {errors.password?.message ?? errors.confirmPassword?.message ?? serverError}
          </span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.password))}
        className="ls-step-form ls-step-form--tight"
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("forgot.newPasswordLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Lock size={18} className="ls-input-icon" />
            <input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder={t("forgot.newPasswordPlaceholder")}
              className="ls-form-input"
              disabled={loading}
              autoFocus
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="ls-eye-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="ls-input-group">
          <label className="ls-input-label">{t("shared.confirmPasswordLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Lock size={18} className="ls-input-icon" />
            <input
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("shared.repeatPasswordPlaceholder")}
              className="ls-form-input"
              disabled={loading}
              autoComplete="new-password"
              onPaste={(e) => e.preventDefault()}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="ls-eye-toggle"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <PasswordChecklist password={watchedPassword} />

        <button
          type="submit"
          className={`ls-btn-primary${loading ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Check size={18} />
          )}
          <span>{loading ? t("forgot.updating") : t("forgot.setNewPassword")}</span>
        </button>
      </form>
    </div>
  );
}
